import { CircularProgress } from "@mui/material";
import { get, includes, map } from "lodash";
import queryString from "query-string";
import { useEffect } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import ErrorMessage from "../../../common/js/components/ErrorMessage";
import comments_operations from "../comments/ducks/operations";
import contactOperations from "../contact/ducks/operations";
import dealActions from "../deal/ducks/actions";
import dealOperations from "../deal/ducks/operations";
import dealsOperations from "../deals/ducks/operations";
import {
    condoConstraints,
    mixedUseContraints,
    portfolioConstraints,
} from "../listing/ducks/listingValidationNonRequired";
import propertyOperations from "../property/ducks/operations";
import searchOperations from "../search/ducks/operations";
import { findAndValidateField } from "../utils/findAndValidateField";
import utils from "../utils/index";
import utilOperations, { displaySellerLandlordCompData } from "../utils/operations";
import AppraisalCompComponent from "./AppraisalCompComponent";
import AppraisalCompToolbar from "./AppraisalCompToolbar";
import CompComponent from "./CompComponent";
import CompToolbar from "./CompToolbar";
import { compConstraintsNonRequired, installmentConstraints } from "./ducks/compValidationNotRequired";
import operations from "./ducks/operations";
import { outsideOrAppraisalCompConstraintsNotRequired } from "./ducks/outsideOrAppraisalCompValidationNonRequired";
import OutsideCompComponent from "./OutsideCompComponent";
import OutsideCompToolbar from "./OutsideCompToolbar";

const CompContainer = (props) => {
    const {
        allowAccess,
        deal,
        getDeal,
        handleClearDealState,
        handleInput,
        handleTotalConsiderationChange,
        setTransactionType,
        toggleTerminationDoc,
        totalConsideration,
        toggleComments,
        submitClicked,
        validateCompByField
    } = props;
    const { id } = useParams();
    const location = useLocation();
    const isOutsideComp = deal?.outsideCompFlag;
    const isAppraisalComp = ["Appraisal Draft", "Appraisal"].includes(deal.compStatus);

    useEffect(() => {
        toggleTerminationDoc(false);
        if (id) {
            getDeal(id);
        } else {
            setTransactionType("LEASE");
        }
        ReactGA.send({ hitType: "pageview", page: "/comp/*", title: `Comp Detail: ${id}` });

        return () => handleClearDealState();
    }, [location]);

    useEffect(() => {
        const queryParams = queryString.parse(location.search);
        const comments = queryParams?.comments ?? false;
        if (comments) {
            toggleComments();
        }
    }, []);

    const handleAutocalculateTiSf = (e) => {
        if (get(deal, "transactionType", "").toLowerCase() === "lease") {
            let tiTotal;
            let sf;
            let tiRate;
            switch (e.target.id) {
                case "tiRate":
                    tiRate = e.target.value ? parseFloat(e.target.value.replace(/[^0-9.]/g, "")) : e.target.value;
                    sf = get(totalConsideration, "squareFeet", 0);
                    if (isNaN(sf)) sf = parseInt(sf.replace(/[^0-9]/g, ""));

                    tiTotal = sf * tiRate;
                    handleInput("tiRate", e.target.value);
                    if (!isNaN(tiRate)) {
                        handleInput("tiTotal", tiTotal);
                    } else {
                        handleInput("tiTotal", "");
                    }
                    break;
                case "squareFeet":
                    sf = e.target.value;
                    if (isNaN(sf)) sf = parseInt(sf.replace(/[^0-9]/g, ""));
                    if (typeof props.deal.tiRate === "string") {
                        tiRate = parseFloat(props.deal.tiRate.replace(/[^0-9.]/g, ""));
                    } else {
                        tiRate = props.deal.tiRate;
                    }
                    tiTotal = tiRate * sf;
                    handleTotalConsiderationChange("squareFeet", e.target.value);
                    if (!isNaN(sf)) {
                        handleInput("tiTotal", tiTotal);
                    } else {
                        handleInput("tiTotal", "");
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const handleBlurValidate = (section, id, value) => {
        if (submitClicked) {
            return validateCompByField(section, id, value);
        }

        const flatConstraints = {
            installment: installmentConstraints,
            mixedUse: mixedUseContraints,
            condo: condoConstraints,
            portfolio: portfolioConstraints,
        };

        if (flatConstraints[section]) {
            const fieldToValidate = section === "installment" && id ? id.split(".").pop() : id;
            return findAndValidateField(flatConstraints[section], fieldToValidate, value);
        }

        const sectionedConstraints =
            isAppraisalComp || isOutsideComp
                ? outsideOrAppraisalCompConstraintsNotRequired
                : compConstraintsNonRequired;

        return findAndValidateField(sectionedConstraints, id, value, section);
    };

    if (allowAccess) {
        return isOutsideComp ? (
            <div style={{ minWidth: "800px" }}>
                <OutsideCompToolbar {...props} transactionType={deal.transactionType} />
                <OutsideCompComponent
                    {...props}
                    id={id}
                    handleAutocalculateTiSf={handleAutocalculateTiSf}
                    handleBlurValidate={handleBlurValidate}
                    isOutsideComp={true}
                />
            </div>
        ) : isAppraisalComp ? (
            <div style={{ minWidth: "800px" }}>
                <AppraisalCompToolbar {...props} transactionType={deal.transactionType} />
                <AppraisalCompComponent
                    {...props}
                    id={id}
                    listing={deal.listing}
                    property={deal.listing.property}
                    handleAutocalculateTiSf={handleAutocalculateTiSf}
                    handleBlurValidate={handleBlurValidate}
                    isAppraisalComp={true}
                />
            </div>
        ) : deal.compStatus ? (
            <div style={{ minWidth: "800px" }}>
                <CompToolbar {...props} transactionType={deal.transactionType} />
                <CompComponent
                    {...props}
                    id={id}
                    handleAutocalculateTiSf={handleAutocalculateTiSf}
                    handleBlurValidate={handleBlurValidate}
                />
            </div>
        ) : (
            <div style={{ textAlign: "center", verticalAlign: "center" }}>
                <CircularProgress style={{ position: "absolute", top: "50%" }} color="primary" />
            </div>
        );
    } else {
        return deal.compStatus ? (
            <ErrorMessage message="You do not have permission to view this comp" />
        ) : (
            <div style={{ textAlign: "center", verticalAlign: "center" }}>
                <CircularProgress style={{ position: "absolute", top: "50%" }} color="primary" />
            </div>
        );
    }
};

const determineIfDisabled = (user, role, personKey, brokersOnDeal, deal) => {
    const brokerPersonKeys = map(brokersOnDeal, (b) => {
        return get(b, "person.personKey");
    });
    const isBrokerOnDeal = includes(brokerPersonKeys, personKey);
    const isCreatedByUser = user === deal.auditCreatedBy;
    const isDataEntryOwner = deal.dataEntryOwner && user === deal.dataEntryOwner;
    const isLastModifiedByUser = user === deal.auditLastModifiedBy;
    const isMigrated = deal.auditCreatedBy === "MIGRATION";

    /**
     * Requirement from 4.18.19:
     *
     * All inside comps that are in status 'Closed' are not editable by anyone. All outside comps are editable in any status by the creator.
     *
     * Throughout the deal workflow, the comp may only be edited by the creator while it is in their 'possession',
     * or by CDT member while they are in 'possession' and they are the data entry owner (status In Review, Admin Action Required),
     *  or by a Data Admin user while it is in their 'possession' (status Final Review).
     *
     * A portland broker admin may edit if comp is in status 'Portland Review Required'
     *
     * New requirement 5.21.19:
     *
     * CDT needs to be able to edit comps in outside and closed statuses.
     */

    //needs to be user who created comp to edit for these statuses
    if (
        includes(
            [
                "Outside",
                "Draft",
                "Broker Action Required",
                "Outside Draft",
                "Portland Review Required",
                "AZ Review Required",
                "Outside",
                "Appraisal Draft",
                "Appraisal",
            ],
            deal.compStatus
        )
    ) {
        if (isCreatedByUser || (isLastModifiedByUser && isMigrated)) {
            return false;
        }
    }

    //needs to be user who created comp OR broker on deal to edit for these statuses
    if (includes([], deal.compStatus)) {
        if (isCreatedByUser || isBrokerOnDeal) {
            return false;
        }
    }

    //needs to be Data Entry Owner to edit for these statuses
    if (includes(["In Review", "Admin Action Required"], deal.compStatus)) {
        if (isDataEntryOwner) {
            return false;
        }
    }

    //needs to be Data Admin to edit for these statuses
    if (includes(["Final Review"], deal.compStatus)) {
        if (role === "dataAdmin") {
            return false;
        }
    }

    //needs to be Portland Broker Admin to edit for these statuses
    if (includes(["Portland Review Required", "Portland Termination Review Required"], deal.compStatus)) {
        if (role === "portlandBrokerAdmin") {
            return false;
        }
    }

    //needs to be Arizona Broker Admin to edit for these statuses
    if (includes(["AZ Review Required"], deal.compStatus)) {
        if (role === "arizonaBrokerAdmin") {
            return false;
        }
    }

    //needs to be Data Entry or Data Admin to edit for these statuses
    if (includes(["Outside", "Closed"], deal.compStatus)) {
        if (includes(["dataEntry", "dataAdmin"], role)) {
            return false;
        }
    }

    //allows research to edit outside comps
    if (includes(["Outside", "Appraisal"], deal.compStatus)) {
        if (includes(["research", "researchAdmin"], role)) {
            return false;
        }
    }

    return true;
};

const displayConfidentialCompData = (user, role, personKey, brokersOnDeal, deal) => {
    const brokerPersonKeys = map(brokersOnDeal, (b) => {
        return get(b, "person.personKey");
    });
    const isBrokerOnDeal = includes(brokerPersonKeys, personKey);
    const isCreatedByUser = user === deal.auditCreatedBy;
    const isDataEntryOwner = deal.dataEntryOwner && user === deal.dataEntryOwner;
    const isLastModifiedByUser = user === deal.auditLastModifiedBy;

    return (
        isBrokerOnDeal ||
        isCreatedByUser ||
        isDataEntryOwner ||
        isLastModifiedByUser ||
        includes(["dataEntry", "dataAdmin", "accounting", "evp", "portlandBrokerAdmin", "arizonaBrokerAdmin"], role)
    );
};

const determineRestrictViewDocuments = (user, role, personKey, brokersOnDeal, deal) => {
    const brokerPersonKeys = map(brokersOnDeal, (b) => {
        return get(b, "person.personKey");
    });
    const isBrokerOnDeal = includes(brokerPersonKeys, personKey);
    const isCreatedByUser = user === deal.auditCreatedBy;
    const isLastModifiedByUser = user === deal.auditLastModifiedBy;
    const isMigrated = deal.auditCreatedBy === "MIGRATION";

    /**
     * Requirement from 4.18.19:
     * Documents on a comp are viewable during the deal workflow by the creator OR a CDT OR Data Admin user regardless of status.
     * Documents are viewable by the creator OR other brokers on the deal when the comp is moved to 'Closed' statuses.
     * Documents are viewable on submitted outside comps by anyone. Only creator and CDT/Data Admin may view while comp is in 'Outside Draft' status
     *
     * Requirement from 6.6.19:
     * RP users should be able to view documents on any comps that are pulled up through the comp search
     */

    //anyone can view any document on outside comp
    if (includes(["Outside", "Appraisal"], deal.compStatus)) {
        return false;
    }

    //if not closed, needs to be user who created comp or data entry
    if (
        includes(
            [
                "Outside Draft",
                "Draft",
                "Broker Action Required",
                "Portland Review Required",
                "Portland Termination Review Required",
                "AZ Review Required",
                "Needs Review",
                "In Review",
                "Admin Action Required",
                "Final Review",
            ],
            deal.compStatus
        )
    ) {
        if (isCreatedByUser || role === "dataEntry" || role === "dataAdmin" || (isLastModifiedByUser && isMigrated)) {
            return false;
        }
    }

    //portland
    if (includes(["Portland Review Required", "Portland Termination Review Required"], deal.compStatus)) {
        if (
            isCreatedByUser ||
            includes(["dataEntry", "dataAdmin", "portlandBrokerAdmin"], role) ||
            (isLastModifiedByUser && isMigrated)
        ) {
            return false;
        }
    }

    //arizona
    if (includes(["AZ Review Required"], deal.compStatus)) {
        if (
            isCreatedByUser ||
            includes(["dataEntry", "dataAdmin", "arizonaBrokerAdmin"], role) ||
            (isLastModifiedByUser && isMigrated)
        ) {
            return false;
        }
    }

    //if closed, needs to be user who created comp OR broker on deal OR RP, dataEntry, or dataAdmin
    if (includes(["Closed"], deal.compStatus)) {
        if (isCreatedByUser || isBrokerOnDeal || role === "dataEntry" || role === "dataAdmin" || role === "evp") {
            return false;
        }
    }

    //needs to be Portland Broker Admin
    if (includes(["Portland Review Required", "Portland Termination Review Required"], deal.compStatus)) {
        if (role === "portlandBrokerAdmin") {
            return false;
        }
    }

    //needs to be AZ Broker Admin
    if (includes(["AZ Review Required"], deal.compStatus)) {
        if (role === "arizonaBrokerAdmin") {
            return false;
        }
    }

    //portland broker admin special case
    if (
        role === "portlandBrokerAdmin" &&
        deal.transactionType === "Sale" &&
        get(deal, "listing.property.primaryAddress.city.state.stateCode") === "OR"
    ) {
        return false;
    }

    return true;
};

const mapStateToProps = (state) => {
    var nextState = {
        userProfile: state.authReducer.userProfile,
        valid: state.authReducer.validFormValues,
        snackbarOpen: state.authReducer.snackbarOpen,
        deal: state.dealReducer.deal,
        installments: state.dealReducer.installments ?? {},
        totalConsideration: state.dealReducer.totalConsideration ?? {},
        invoice: state.dealReducer.invoice ? state.dealReducer.invoice : null,
        propertyAttributes: state.dealReducer.propertyAttributes ?? {},
        listingAttributes: get(state, "dealReducer.deal.listing.attributes", null)
            ? state.dealReducer.deal.listing.attributes
            : {},
        property: state.propertyReducer.property ?? {},
        errors: state.dealReducer.errors ?? {},
        submitClicked: state.dealReducer.submitClicked,
        expandedErrorPanels: state.utilsReducer.panelsToExpand,
        checkErrorPanels: state.utilsReducer.checkErrorPanels,
        searchResults: state.dealReducer.searchResults,
        autocompleteResults: state.searchReducer.autocompleteResults,
        companyInformation: state.dealReducer.companyInformation ?? {},
        executeInvoiceModalOpen: state.dealReducer.executeInvoiceModalOpen
            ? state.dealReducer.executeInvoiceModalOpen
            : false,
        isSaving: state.utilsReducer.isSaving,
        saveError: state.compReducer.saveError ?? null,
        lastSaved: state.compReducer.lastSaved ?? null,
        numComments: state.commentsReducer.comments.length,
        snackbar: state.utilsReducer.snackbar,
        documentWarning: state.compReducer.documentWarning,
        confirmDialog: state.utilsReducer.confirmDialog,
        elementsSaving: state.utilsReducer.elementsSaving,
        terminateDialog: state.dealReducer.terminateDialog,
        confirmTerminateDialog: state.dealReducer.confirmTerminateDialog,
        terminationDocument: state.dealReducer.terminationDocument,
        deleteOutsideCompDialog: state.compReducer.deleteOutsideCompDialog,
        brokersOnDeal: state.dealReducer.brokersOnDeal,
        disableInputs: determineIfDisabled(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        restrictViewDocuments: determineRestrictViewDocuments(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        allowAccess: utilOperations.compAccessAllowed(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        displayConfidentialCompData: displayConfidentialCompData(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        displaySellerLandlordCompData: displaySellerLandlordCompData(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        isRequiredDocsLoaded: state.documentsReducer.requiredDocs !== null,
        adminReviewType: state.dealReducer.deal.adminReviewType,
        isStatusTrackerVisible: utilOperations.isStatusTrackerVisible(
            state.utilsReducer?.statusTracker?.isActive,
            state.authReducer.userProfile.role
        ),
    };
    return nextState;
};

const mapDispatchToProps = (dispatch) => {
    return {
        handleAddContactInfo(contactType) {
            dispatch(contactOperations.addContact(contactType));
        },
        handleContactInfoCompanyUpdate: (contactType, key, field, value) => {
            dispatch(contactOperations.updateContactCompany(contactType, key, field, value));
        },
        getDeal: (dealKey) => {
            dispatch(operations.getDeal(dealKey));
        },
        setTransactionType: (type) => {
            dispatch(dealOperations.dealChange("transactionType", type));
        },
        handleSearchCompany: (inputValue, companyType, excludeParentCompanies = false) => {
            dispatch(searchOperations.searchCompany(inputValue, companyType, excludeParentCompanies));
        },
        handleSearchPerson: (inputValue) => {
            dispatch(searchOperations.searchKMPersonAutocomplete(inputValue));
        },
        handleSearchKMBrokers: (inputValue) => {
            dispatch(searchOperations.searchKMBrokers(inputValue));
        },
        handleSearchOutsideBrokers: (inputValue) => {
            dispatch(searchOperations.searchOutsideBroker(inputValue));
        },
        handleObjectPropertyChange: (objectPropertyName, event) => {
            dispatch(
                dealOperations.handleObjectPropertyChange(objectPropertyName, event.target.id, event.target.value)
            );
        },
        handleCheckboxClick: (name, event) => {
            dispatch(dealOperations.dealChange(name, event.target.checked));
        },
        handleConfidential: (field, value) => {
            dispatch(dealOperations.dealChange(field, value));
        },
        handleDealChange: (event) => {
            dispatch(dealOperations.dealChange(event.target.id, event.target.value));
        },
        handlePropertyDetailChange: (field, value) => {
            dispatch(operations.handlePropertyDetailsChange(field, value));
        },
        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handleDocumentWarning: (open, proceed, docs) => {
            dispatch(operations.handleDocumentWarning(open, proceed, docs));
        },
        handleConfirmWarning: () => {
            dispatch(operations.handleDocumentWarning(false, true, []));
        },
        toggleComments: () => {
            dispatch(comments_operations.toggleComments());
        },
        handleToggleBooleanProperty: (field) => {
            dispatch(dealOperations.handleToggleBooleanProperty(field));
        },
        handleExecuteInvoice: () => {
            dispatch(dealOperations.executeInvoice());
        },
        handleTotalConsiderationChange: (field, value) => {
            dispatch(dealOperations.handleTotalConsiderationChange(field, value));
        },
        handleCalculateTotalConsiderationDetails() {
            dispatch(dealOperations.handleCalculateTotalConsiderationDetails());
        },
        handleClearRentSchedule() {
            dispatch(dealOperations.handleClearRentSchedule());
        },
        handlePeriodMonthChange: (periodNumber, month, field, value) => {
            dispatch(dealOperations.handlePeriodMonthChange(periodNumber, month, field, value));
        },
        handleCompanyPropertyChange: (companyType, property, value) => {
            dispatch(dealOperations.handleCompanyPropertyChange(companyType, property, value));
        },
        submitOutsideOrAppraisalComp: (callback, skipRequiredValidation, submitStatus, compType) => {
            return dispatch(
                operations.handlePreOutsideOrAppraisalCompSubmissionTasks({
                    callback: callback,
                    skipRequiredValidation: skipRequiredValidation,
                    elementSaving: "submit",
                    submitStatus: submitStatus,
                }),
                compType
            );
        },
        saveOutsideOrAppraisalComp: (compType, skipRequiredValidation) => {
            dispatch(
                operations.handlePreOutsideOrAppraisalCompSubmissionTasks(
                    {
                        callback: null,
                        skipRequiredValidation: skipRequiredValidation,
                        elementSaving: "saveAndContinue",
                    },
                    compType
                )
            );
        },
        saveAndExitOutsideOrAppraisalComp: (compType, skipRequiredValidation) => {
            return dispatch(
                operations.handlePreOutsideOrAppraisalCompSubmissionTasks(
                    { callback: null, skipRequiredValidation: skipRequiredValidation, elementSaving: "saveAndExit" },
                    compType
                )
            );
        },
        submitInsideComp: (callback, skipRequiredValidation) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(
                operations.handlePreCompSubmissionTasks({
                    callback: callback,
                    skipRequiredValidation: skipRequiredValidation,
                    elementSaving: "submit",
                })
            );
        },
        saveInsideComp: () => {
            dispatch(
                operations.handlePreCompSubmissionTasks({
                    callback: null,
                    skipRequiredValidation: true,
                    elementSaving: "saveAndContinue",
                })
            );
        },
        saveAndExitInsideComp: () => {
            return dispatch(
                operations.handlePreCompSubmissionTasks({
                    callback: null,
                    skipRequiredValidation: true,
                    elementSaving: "saveAndExit",
                })
            );
        },
        handleInput: (path, value) => {
            dispatch(operations.handleInput("deal." + path, value));
        },
        handleClearDealState: () => {
            dispatch(operations.handleInput("deal", {}));
            dispatch(dealActions.clearErrors());
            dispatch(dealOperations.clearTotalConsiderationState());
        },
        validateCompByField: (section, id, value, options) => {
            return dispatch(operations.validateCompByField(section, id, value, options));
        },
        handleAddArrayElement: (path, obj) => {
            dispatch(operations.addArrayElement("deal." + path, obj));
        },
        handleDeleteArrayElement: (path, index) => {
            dispatch(operations.deleteArrayElement("deal." + path, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInput("deal." + arrayPath + "[" + index + "]." + field, value));
        },
        toggleCheckErrorPanels: (value) => {
            dispatch(utils.actions.toggleCheckErrorPanels(value));
        },
        handleStartReviewingAsCorporateData: (deal) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(dealsOperations.handleStartReviewingAsCorporateData(deal));
        },
        handleClearTotalConsiderationState: () => {
            dispatch(dealOperations.clearTotalConsiderationState());
        },
        togglePropertySearchModal: (isOpen) => {
            dispatch(propertyOperations.togglePropertySearchModal(isOpen));
        },
        toggleTerminateDialog: (isOpen, dealStatus) => {
            dispatch(dealOperations.toggleTerminateDialog(isOpen, dealStatus));
        },
        handleTerminateDeal: (dealKey, terminationDocument) => {
            return dispatch(dealOperations.verifyDocumentAndTerminatePendingDeal(dealKey, terminationDocument));
        },
        toggleTerminationDoc: (attached) => {
            dispatch(dealOperations.terminationDocUpload(attached));
        },
        handleConfirmTerminateDeal: (dealKey, terminationDocument, dealStatus) => {
            return dispatch(dealOperations.verifyDocumentAndTerminateDeal(dealKey, terminationDocument, dealStatus));
        },
        handleDisplayDeleteDialog: (isOpen) => {
            dispatch(operations.displayDeleteDialog(isOpen));
        },
        handleDeleteOutsideComp: (dealKey) => {
            return dispatch(operations.handleDeleteOutsideComp(dealKey));
        },
        handlePeriodChange: (period, field, value) => {
            dispatch(dealOperations.handlePeriodChange(period, field, value));
        },
        handleRepresentationTypeChange: (path, value, dealKey) => {
            dispatch(operations.handleInput("deal." + path, value));
            dispatch(dealOperations.updateDealFields(dealKey, { representationType: value }));
        },
        getStatusTrackerData: (entityKey, entityType, abortSignal) => {
            dispatch(utilOperations.fetchStatusTrackerData(entityKey, entityType, abortSignal));
        },
        clearStatusTracker: () => {
            dispatch(utilOperations.deactivateStatusTracker());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompContainer);
